import state, { getValue, setValue } from './state';
import { UTF8ArrayToString } from './utils';

const midpointTestStr = 'ABCDEFGHIKLMNOPRSTUVWXYZ0123456789';

class ViewModel {
  constructor({ name, colourOverrides, onPresetAdded, onUndoRedoEnabled, onInitialise, onMove, onCompleted }) {
    this._name = name;
    this._model = null;
    this._onscreenCanvas = null;
    this._offscreenCanvas = null;
    this._offscreenCanvasContext = null;
    this._updateXMin = undefined;
    this._updateXMax = undefined;
    this._updateYMin = undefined;
    this._updateYMax = undefined;
    this._midpointCache = [];
    this._timer = null;
    this._timerReference = null;
    this._timerCallback = null;
    this._blittercount = 0;
    this._blitters = new Map();
    this._saveFileReadCallback = null;
    this._checkpoint = null;
    this._checkpointMoves = null;
    this._solution = [];
    this._boardSizePreset = 0;
    this._colourOverrides = colourOverrides;
    this._onPresetAdded = onPresetAdded;
    this._onUndoRedoEnabled = onUndoRedoEnabled;
    this._onInitialise = onInitialise;
    this._onMove = onMove;
    this._onCompleted = onCompleted;
    this._command = null;
    this._loadGame = null;
    this._getSaveFile = null;
    this._freeSaveFile = null;
    this._setProperty = null;
    this._setFlag = null;
    this._rectangles = [];
    this._lines = [];
    this._width = null;
    this._height = null;
  }

  get onscreenCanvas() {
    this._onscreenCanvas ||= document.getElementById('puzzle-canvas');
    return this._onscreenCanvas;
  }

  get offscreenCanvas() {
    this._offscreenCanvas ||= document.createElement('canvas');
    return this._offscreenCanvas;
  }

  get offscreenCanvasContext() {
    this._offscreenCanvasContext ||= this.offscreenCanvas.getContext('2d', { alpha: false });
    return this._offscreenCanvasContext;
  }

  get name() { return this._name; }

  // debug = new Set()
  getColour(colourPtr) {
    const colour = this.UTF8ToString(colourPtr);

    // ToDo: use this for debugging colour overrides
    // if (!this.debug.has(colour)) {
    //   console.log(colour)
    //   this.debug.add(colour);
    // }

    return this._colourOverrides.get(colour) ?? colour;
  }

  set boardSizePreset(value) {
    this._boardSizePreset = value;
    this.clearCheckpoint();
    this.command(2);
    this._precacheSolution();

  }

  set options(options) {
    this._boardSizePreset = -1;
    this.clearCheckpoint();
    this.command(2);

    for (const option of options) {
      switch (option.type) {
        case 'boolean':
          this.setNumericProperty(option.index, option.value ? 1 : 0);
          break;
        case 'number':
          this.setStringProperty(option.index, option.value.toString());
          break;
        case 'choices':
          this.setNumericProperty(option.index, option.value);
          break;
      }
    }

    this.command(3);
    this._precacheSolution();
  }

  getEnvStrings() {
    if (!this.getEnvStrings.strings) {
      const lang = `${(typeof navigator === 'object' && navigator.languages && navigator.languages[0] || 'C').replace('-', '_')}.UTF-8`;
      const env = {
        'USER': 'web_user',
        'LOGNAME': 'web_user',
        'PATH': '/',
        'PWD': '/',
        'HOME': '/home/web_user',
        'LANG': lang,
      };

      const strings = [];
      for (let x in env) {
        strings.push(`${x}=${env[x]}`);
      }

      this.getEnvStrings.strings = strings;
    }

    return this.getEnvStrings.strings;
  }

  canvasMouseCoordinates(event, element) {
    const absolutePosition = (element) => {
      let ex = 0;
      let ey = 0;
      while (element.offsetParent) {
        ex += element.offsetLeft;
        ey += element.offsetTop;
        element = element.offsetParent;
      }

      return { x: ex, y: ey };
    }

    const eCoords = absolutePosition(element);
    const rCoords = {
      x: event.pageX - eCoords.x,
      y: event.pageY - eCoords.y
    };

    const xScale = element.width / element.offsetWidth;
    const yScale = element.height / element.offsetHeight;

    return {
      x: rCoords.x * xScale,
      y: rCoords.y * yScale,
    };
  }

  /**
   * Given a pointer 'ptr' to a null-terminated UTF8-encoded string in the emscripten HEAP,
   * returns a copy of that string as a Javascript String object.
   *
   * @param {number} ptr
   * @param {number=} maxBytesToRead - An optional length that specifies the
   *   maximum number of bytes to read. You can omit this parameter to scan the
   *   string until the first 0 byte. If maxBytesToRead is passed, and the string
   *   at [ptr, ptr+maxBytesToReadr[ contains a null byte in the middle, then the
   *   string will cut short at that byte index (i.e. maxBytesToRead will not
   *   produce a string of exact length [ptr, ptr+maxBytesToRead[) N.B. mixing
   *   frequent uses of UTF8ToString() with and without maxBytesToRead may throw
   *   JS JIT optimizations off, so it is worth to consider consistently using one
   * @return {string}
   */
  UTF8ToString(ptr, maxBytesToRead) {
    return ptr ? UTF8ArrayToString(state['HEAPU8'], ptr, maxBytesToRead) : '';
  }

  wasmWrap(func, returnType, argTypes) {
    return (...args) => {
      const toC = {
        'string': str => {
          let ret = 0;
          if (str !== null && str !== undefined && str !== 0) {
            ret = this._model.stringToUTF8OnStack(str);
          }
          return ret;
        },
        'array': arr => {
          const ret = this._model.emscriptenStackAlloc(arr.length);
          state['HEAP8'].set(arr, ret);
          return ret;
        },
      };

      const cArgs = [];
      let stack = 0;
      if (args) {
        for (let i = 0; i < args.length; i++) {
          const converter = toC[argTypes[i]];
          if (converter) {
            if (stack === 0) stack = this._model.emscriptenStackGetCurrent();
            cArgs[i] = converter(args[i]);
          } else {
            cArgs[i] = args[i];
          }
        }
      }

      let ret = this._model[func](...cArgs);
      if (stack !== 0) {
        this._model.emscriptenStackRestore(stack);
      }

      if (returnType === 'string') return this.UTF8ToString(ret);
      if (returnType === 'boolean') return Boolean(ret);
      return ret;
    };
  }

  assertFail(condition, filename, line, func) {
    throw new WebAssembly.RuntimeError(`Assertion failed: ${this.UTF8ToString(condition)}, at: ${[filename ? this.UTF8ToString(filename) : 'unknown filename', line, func ? this.UTF8ToString(func) : 'unknown function']}`);
  }

  emscriptenMemcpyJs(dest, src, num) {
    return state['HEAPU8'].copyWithin(dest, src, src + num);
  }

  emscriptenResizeHeap(requestedSize) {
    const oldSize = state['HEAPU8'].length;
    // With CAN_ADDRESS_2GB or MEMORY64, pointers are already unsigned.
    requestedSize >>>= 0;
    // With multithreaded builds, races can happen (another thread might increase the size in between),
    // so return a failure, and const the caller retry.

    // Memory resize rules:
    // 1.  Always increase heap size to at least the requested size, rounded up to next page multiple.
    // 2a. If MEMORY_GROWTH_LINEAR_STEP == -1, excessively resize the heap geometrically:
    //   increase the heap size according to MEMORY_GROWTH_GEOMETRIC_STEP factor (default +20%).
    //   At most overreserve by MEMORY_GROWTH_GEOMETRIC_CAP bytes (default 96MB).
    // 2b. If MEMORY_GROWTH_LINEAR_STEP != -1, excessively resize the heap linearly:
    //   increase the heap size by at least MEMORY_GROWTH_LINEAR_STEP bytes.
    // 3.  Max size for the heap is capped at 2048MB-WASM_PAGE_SIZE, or by MAXIMUM_MEMORY,
    //   or by ASAN limit, depending on which is smallest
    // 4.  If we were unable to allocate as much memory, it may be due to over-eager decision to excessively reserve due to (3) above.
    //   Hence if an allocation fails, cut down on the amount of excess growth,
    //   in an attempt to succeed to perform a smaller allocation.

    // Stay one Wasm page short of 4GB: while e.g. Chrome is able to allocate full 4GB Wasm memories,
    // the size will wrap back to 0 bytes in Wasm side for any code that deals with heap sizes,
    // which would require special casing all heap size related code to treat 0 specially.
    const maxHeapSize = 2147483648;
    if (requestedSize > maxHeapSize) {
      console.error(`Cannot enlarge memory, requested ${requestedSize} bytes, but the limit is ${maxHeapSize} bytes!`);
      return false;
    }

    const alignUp = (x, multiple) => x + (multiple - x % multiple) % multiple;

    for (let cutDown = 1; cutDown <= 4; cutDown *= 2) {
      let overGrownHeapSize = oldSize * (1 + 0.2 / cutDown); // ensure geometric growth
      overGrownHeapSize = Math.min(overGrownHeapSize, requestedSize + 100663296);

      const newSize = Math.min(maxHeapSize, alignUp(Math.max(requestedSize, overGrownHeapSize), 65536));
      if (this._model.growMemory(newSize)) {
        return true;
      }
    }

    console.error(`Failed to grow the heap - not enough memory!`);
    return false;
  }

  environGet(environ, environ_buf) {
    let bufSize = 0;
    this.getEnvStrings().forEach((string, i) => {
      let ptr = environ_buf + bufSize;
      state['HEAPU32'][environ + i * 4 >> 2] = ptr;

      for (let i = 0; i < string.length; ++i) {
        state['HEAP8'][ptr++] = string.charCodeAt(i);
      }

      state['HEAP8'][ptr] = 0; // null-terminate the string
      bufSize += string.length + 1;
    });

    return 0;
  }

  environSizesGet(pEnvironCount, pEnvironBufSize) {
    const strings = this.getEnvStrings();
    state['HEAPU32'][pEnvironCount >> 2] = strings.length;

    let bufSize = 0;
    strings.forEach(string => bufSize += string.length + 1);
    state['HEAPU32'][pEnvironBufSize >> 2] = bufSize;

    return 0;
  }

  jsActivateTimer() {
    this._timerReference = performance.now();

    const frame = (now) => {
      this._timer = window.requestAnimationFrame(frame);
      this._timerCallback((now - this._timerReference) / 1000.0);
      this._timerReference = now;
    };

    this._timer = window.requestAnimationFrame(frame);
  }

  jsAddPreset(menuId, ptr, value) {
    const name = this.UTF8ToString(ptr);
    this._onPresetAdded(name, value);
  }

  jsCanvasClip(x, y, w, h) {
    this.offscreenCanvasContext.save();
    this.offscreenCanvasContext.beginPath();
    this.offscreenCanvasContext.rect(x, y, w, h);
    this.offscreenCanvasContext.clip();
  }

  jsCanvasDrawCircle(x, y, r, fill, outline) {
    this.offscreenCanvasContext.beginPath();
    this.offscreenCanvasContext.arc(x + 0.5, y + 0.5, r, 0, 2 * Math.PI);

    if (fill >= 0) {
      this.offscreenCanvasContext.fillStyle = this.getColour(fill);
      this.offscreenCanvasContext.fill();
    }

    this.offscreenCanvasContext.lineWidth = 1;
    this.offscreenCanvasContext.lineCap = 'round';
    this.offscreenCanvasContext.lineJoin = 'round';
    this.offscreenCanvasContext.strokeStyle = this.getColour(outline);
    this.offscreenCanvasContext.stroke();
  }

  jsCanvasDrawLine(x1, y1, x2, y2, width, colourPtr) {
    const colour = this.getColour(colourPtr);
    this.offscreenCanvasContext.beginPath();
    this.offscreenCanvasContext.moveTo(x1 + 0.5, y1 + 0.5);
    this.offscreenCanvasContext.lineTo(x2 + 0.5, y2 + 0.5);
    this.offscreenCanvasContext.lineWidth = width;
    this.offscreenCanvasContext.lineCap = 'round';
    this.offscreenCanvasContext.lineJoin = 'round';
    this.offscreenCanvasContext.strokeStyle = colour;
    this.offscreenCanvasContext.stroke();
    this.offscreenCanvasContext.fillStyle = colour;
    this.offscreenCanvasContext.fillRect(x1, y1, 1, 1);
    this.offscreenCanvasContext.fillRect(x2, y2, 1, 1);
    this._lines.push({ x1, y1, x2, y2, w: width, colour });
  }

  jsCanvasDrawPoly(pointPtr, nPoints, fillPtr, outlinePtr) {
    const fill = this.getColour(fillPtr);
    const outline = this.getColour(outlinePtr);

    this.offscreenCanvasContext.beginPath();
    this.offscreenCanvasContext.moveTo(getValue(pointPtr, 'i32') + 0.5, getValue(pointPtr + 4, 'i32') + 0.5);

    for (let i = 1; i < nPoints; i++) {
      this.offscreenCanvasContext.lineTo(getValue(pointPtr + 8 * i, 'i32') + 0.5, getValue(pointPtr + 8 * i + 4, 'i32') + 0.5);
    }

    this.offscreenCanvasContext.closePath();
    if (fillPtr >= 0) {
      this.offscreenCanvasContext.fillStyle = fill;
      this.offscreenCanvasContext.fill();
    }

    this.offscreenCanvasContext.lineWidth = 1;
    this.offscreenCanvasContext.lineCap = 'round';
    this.offscreenCanvasContext.lineJoin = 'round';
    this.offscreenCanvasContext.strokeStyle = outline;
    this.offscreenCanvasContext.stroke();
  }

  jsCanvasDrawRect(x, y, w, h, colourPtr) {
    const colour = this.getColour(colourPtr);
    this.offscreenCanvasContext.fillStyle = colour;
    this.offscreenCanvasContext.fillRect(x, y, w, h);
    this._rectangles.push({ x, y, w, h, colour });
  }

  jsCanvasDrawText(x, y, hAlign, colourPtr, fontPtr, textPtr) {
    this.offscreenCanvasContext.font = this.UTF8ToString(fontPtr);
    this.offscreenCanvasContext.fillStyle = this.getColour(colourPtr);
    this.offscreenCanvasContext.textAlign = (hAlign === 0
      ? 'left'
      : hAlign === 1
        ? 'center'
        : 'right');
    this.offscreenCanvasContext.textBaseline = 'alphabetic';
    this.offscreenCanvasContext.fillText(this.UTF8ToString(textPtr), x, y);
  }

  jsCanvasDrawUpdate(x, y, w, h) {
    if (this._updateXMin === undefined || this._updateXMin > x) this._updateXMin = x;
    if (this._updateYMin === undefined || this._updateYMin > y) this._updateYMin = y;
    if (this._updateXMax === undefined || this._updateXMax < x + w) this._updateXMax = x + w;
    if (this._updateYMax === undefined || this._updateYMax < y + h) this._updateYMax = y + h;
  }

  jsCanvasEndDraw() {
    if (!this.onscreenCanvas) {
      return;
    }

    if (this._updateXMin !== undefined) {
      const onscreenCtx = this.onscreenCanvas.getContext('2d', {
        alpha: false
      });

      onscreenCtx.drawImage(
        this.offscreenCanvas,
        this._updateXMin,
        this._updateYMin,
        this._updateXMax - this._updateXMin,
        this._updateYMax - this._updateYMin,
        this._updateXMin,
        this._updateYMin,
        this._updateXMax - this._updateXMin,
        this._updateYMax - this._updateYMin);
    }
  }

  jsCanvasFindFontMidpoint(height, fontPtr) {
    if (!this.onscreenCanvas) {
      return;
    }

    const font = this.UTF8ToString(fontPtr);

    if (this._midpointCache[font] !== undefined) {
      return this._midpointCache[font];
    }

    // find the width of the string
    const ctx1 = this.onscreenCanvas.getContext('2d');
    ctx1.font = font;
    const width = (ctx1.measureText(midpointTestStr).width + 1) | 0;

    // construct a test canvas of appropriate size, initialise it to black, and draw the string on it in white
    const measureCanvas = document.createElement('canvas');
    const ctx2 = measureCanvas.getContext('2d');
    ctx2.canvas.width = width;
    ctx2.canvas.height = 2 * height;
    ctx2.fillStyle = "#000000";
    ctx2.fillRect(0, 0, width, 2 * height);

    const baseline = (1.5 * height) | 0;
    ctx2.fillStyle = "#ffffff";
    ctx2.font = font;
    ctx2.fillText(midpointTestStr, 0, baseline);

    // scan the contents of the test canvas to find the top and bottom set pixels
    const pixels = ctx2.getImageData(0, 0, width, 2*height).data;
    let yMin = 2 * height, yMax = -1;
    for (let y = 0; y < 2*height; y++) {
      for (let x = 0; x < width; x++) {
        if (pixels[4*(y*width+x)] !== 0) {
          if (yMin > y) yMin = y;
          if (yMax < y) yMax = y;
          break;
        }
      }
    }

    const ret = (baseline - (yMin + yMax) / 2) | 0;
    this._midpointCache[font] = ret;
    return ret;
  }

  jsCanvasFreeBlitter(bl) {
    this._blitters.delete(bl);
  }

  jsCanvasCopyToBlitter(id, x, y, w, h) {
    const blitterCtx = this._blitters.get(id).getContext('2d');
    blitterCtx.drawImage(
      this.offscreenCanvas,
      x, y, w, h,
      0, 0, w, h);
  }

  jsCanvasCopyFromBlitter(id, x, y, w, h) {
    this.offscreenCanvasContext.drawImage(this._blitters.get(id),
      0, 0, w, h,
      x, y, w, h);
  }

  jsCanvasGetPreferredSize(wp, hp) {
    const dpr = window.devicePixelRatio || 1
    const containingDiv = document.getElementById('puzzle-container');

    setValue(wp, containingDiv.clientWidth * dpr, 'i32');
    setValue(hp, containingDiv.clientHeight * dpr, 'i32');

    return true;
  }

  jsCanvasNewBlitter(w, h) {
    const newBlitter = document.createElement('canvas');
    newBlitter.width = w;
    newBlitter.height = h;

    const id = this._blittercount++;
    this._blitters.set(id, newBlitter);
    return id;
  }

  jsCanvasSetSize(w, h) {
    if (!this.onscreenCanvas) {
      return;
    }

    this.onscreenCanvas.width = w;
    this.onscreenCanvas.height = h;

    this.offscreenCanvas.width = w;
    this.offscreenCanvas.height = h;

    this.offscreenCanvasContext.resetTransform();
  }

  jsCanvasStartDraw() {
    this._updateXMin = this._updateXMax = this._updateYMin = this._updateYMax = undefined
  }

  jsNewGame(statePtr) {
    this._onInitialise && this._onInitialise(this.UTF8ToString(statePtr));
  }

  jsMove(movePtr) {
    this._onMove && this._onMove(this.UTF8ToString(movePtr));
  }

  jsCompleted() {
    this._onCompleted && this._onCompleted();
  }

  jsCanvasClipRect(x, y, w, h) {
    this.offscreenCanvasContext.save();
    this.offscreenCanvasContext.beginPath();
    this.offscreenCanvasContext.rect(x, y, w, h);
    this.offscreenCanvasContext.clip();
  }

  jsCanvasUnclip() {
    this.offscreenCanvasContext.restore()
  }

  jsDeactivateTimer() {
    window.cancelAnimationFrame(this._timer);
  }

  jsDefaultColour(output) {
    const col = window.getComputedStyle(this.onscreenCanvas).backgroundColor;

    // we only support opaque sRGB colours.
    const m = col.match(/^rgb\((\d+(?:\.\d+)?), (\d+(?:\.\d+)?), (\d+(?:\.\d+)?)\)$/);

    if (m) {
      setValue(output, +m[1] / 255, 'float');
      setValue(output + 4, +m[2] / 255, 'float');
      setValue(output + 8, +m[3] / 255, 'float');
    }
  }

  jsFocusCanvas() {
    this.onscreenCanvas.focus()
  }

  jsGetDate64(ptr) {
    const d = (new Date).valueOf();
    setValue(ptr, d, 'i64')
  }

  jsGetDevicePixelRatio() {
    return window.devicePixelRatio || 1;
  }

  jsGetSelectedPreset() {
    return this._boardSizePreset;
  }

  jsPostInit() {
    let mql = null;
    const updatePixelRatio = () => {
      const dpr = window.devicePixelRatio;
      if (mql !== null) mql.removeListener(updatePixelRatio);
      mql = window.matchMedia(`(resolution: ${dpr}dppx)`);
      mql.addListener(updatePixelRatio);
    };

    updatePixelRatio();
    this.onscreenCanvas.focus();
  }

  jsSaveFileRead(buf, len) {
    return this._saveFileReadCallback(buf, len);
  }

  jsSelectPreset(n) {
    this._boardSizePreset = n;
  }

  resizePuzzle(w, h) {
    const canvas = document.getElementById('puzzle-canvas');
    this._width ||= canvas.width;
    this._height ||= canvas.height;

    // ToDo: why is resizePuzzle called for a new game with same size?
    if (this._width !== w || this._height !== h) {
      this._rectangles.length = 0;
    }

    this._model.resizePuzzle(w, h);

    this._width = w;
    this._height = h;
  }

  init(model) {
    this._model = model;

    if (!this.onscreenCanvas) {
      return;
    }

    this.onscreenCanvas.oncontextmenu = () => false;

    const mousedown = this.wasmWrap('mousedown', 'boolean', ['number', 'number', 'number']);
    const buttonPhys2Log = [null, null, null];
    const buttonsDown = () => {
      let i, toRet = 0;
      for (i = 0; i < 3; i++) {
        if (buttonPhys2Log[i] !== null) {
          toRet |= 1 << buttonPhys2Log[i];
        }
      }

      return toRet;
    };

    /////////////////////////////////////
    // desktop/mouse + keyboard events //
    /////////////////////////////////////

    this.onscreenCanvas.onpointerdown = (event) =>  this.onscreenCanvas.setPointerCapture(event.pointerId);

    this.onscreenCanvas.onmousedown = (event) => {
      if (event.button >= 3) {
        return;
      }

      const xy = this.canvasMouseCoordinates(event,  this.onscreenCanvas);

      let mouseButton = event.button;
      if (event.shiftKey) {
        mouseButton = 1; // middle
      } else if (event.ctrlKey) {
        mouseButton = 2; // right
      }

      if (mousedown(xy.x, xy.y, mouseButton)) {
        event.preventDefault();
      }

      buttonPhys2Log[event.button] = mouseButton;
      if (this.onscreenCanvas.setCapture !== undefined &&  this.onscreenCanvas.setPointerCapture === undefined) {
        this.onscreenCanvas.setCapture(true);
      }
    };

    const mouseMove = this.wasmWrap('mousemove', 'boolean', ['number', 'number', 'number']);
    this.onscreenCanvas.onmousemove = (event) => {
      const down = buttonsDown();
      if (down) {
        const xy = this.canvasMouseCoordinates(event,  this.onscreenCanvas);
        if (mouseMove(xy.x, xy.y, down)) {
          event.preventDefault();
        }
      }
    };

    const mouseUp = this.wasmWrap('mouseup', 'boolean', ['number', 'number', 'number']);
    this.onscreenCanvas.onmouseup = (event) => {
      if (event.button >= 3) {
        return;
      }

      if (buttonPhys2Log[event.button] !== null) {
        const xy = this.canvasMouseCoordinates(event,  this.onscreenCanvas);
        if (mouseUp(xy.x, xy.y, buttonPhys2Log[event.button])) {
          event.preventDefault();
        }

        buttonPhys2Log[event.button] = null;
      }
    };

    let isDragging = false;
    this.onscreenCanvas.style.touchAction = 'none';

    this.onscreenCanvas.addEventListener('pointerdown', (event) => {
      isDragging = true;

      let mouseButton = event.button;
      if (event.shiftKey) {
        mouseButton = 1; // middle
      } else if (event.ctrlKey) {
        mouseButton = 2; // right
      }

      const xy = this.canvasMouseCoordinates(event,  this.onscreenCanvas);
      mousedown(xy.x, xy.y, mouseButton);

      event.preventDefault();
    });

    this.onscreenCanvas.addEventListener('pointermove', (event) => {
      if (!isDragging) {
        return;
      }

      const xy = this.canvasMouseCoordinates(event,  this.onscreenCanvas);
      mouseMove(xy.x, xy.y, 1); // 1 to indicate dragging

      event.preventDefault();
    });

    this.onscreenCanvas.addEventListener('pointerup', (event) => {
      if (!isDragging) {
        return;
      }

      isDragging = false;

      const xy = this.canvasMouseCoordinates(event,  this.onscreenCanvas);
      mouseUp(xy.x, xy.y, 0);

      event.preventDefault();
    });

    this.onscreenCanvas.addEventListener('pointercancel', () => isDragging = false);
    this._timerCallback = this.wasmWrap('timerCallback', 'void', ['number']);
  }

  command(index) {
    if (!this._command) {
      this._command = this.wasmWrap('command', 'void', ['number']);
    }

    return this._command(index);
  }

  loadGame(bufferPtr, length) {
    if (!this._loadGame) {
      this._loadGame = this.wasmWrap('loadGame', 'void', ['number', 'number']);
    }

    this._loadGame(bufferPtr, length);
  }

  getSaveFile() {
    if (!this._getSaveFile) {
      this._getSaveFile = this.wasmWrap('getSaveFile', 'number', []);
    }

    return this._getSaveFile();
  }

  freeSaveFile(ptr) {
    if (!this._freeSaveFile) {
      this._freeSaveFile = this.wasmWrap('freeSaveFile', 'void', ['number']);
    }

    this._freeSaveFile(ptr);
  }

  setStringProperty(index, value) {
    if (!this._setProperty) {
      this._setProperty = this.wasmWrap('dlgReturnSVal', 'void', ['number', 'string']);
    }

    this._setProperty(index, value);
  }

  setNumericProperty(index, value) {
    if (!this._setFlag) {
      this._setFlag = this.wasmWrap('dlgReturnIVal', 'void', ['number', 'number']);
    }

    this._setFlag(index, value);
  }

  saveCheckpoint() {
    const savedStatePtr = this.getSaveFile();
    this._checkpoint = this.UTF8ToString(savedStatePtr);
    this.freeSaveFile(savedStatePtr);
    this._checkpointMoves = this.state;
  }

  clearCheckpoint() {
    this._checkpoint = null;
    this._checkpointMoves = null;
  }

  isAtCheckpoint() {
    if (!this._checkpoint || !this._checkpointMoves) {
      return false;
    }

    const moves = this.state;
    if (moves.length !== this._checkpointMoves.length) {
      return false;
    }

    return moves.every((move, index) => move === this._checkpointMoves[index]);
  }

  restoreCheckpoint() {
    if (!this._checkpoint) {
      return;
    }

    const checkpointByteArray = new TextEncoder().encode(this._checkpoint);
    const checkpointPtr = this._model.emscriptenStackAlloc(checkpointByteArray.length);
    state['HEAP8'].set(checkpointByteArray, checkpointPtr);

    this.loadGame(checkpointPtr, checkpointByteArray.length);
    this.freeSaveFile(checkpointPtr);
  }

  getSaveFileString() {
    const savedStatePtr = this.getSaveFile();
    const saveString = this.UTF8ToString(savedStatePtr);
    this.freeSaveFile(savedStatePtr);
    return saveString;
  }

  resumeGame(saveString) {
    const load = (save) => {
      const bytes = new TextEncoder().encode(save);
      const ptr = this._model.emscriptenStackAlloc(bytes.length);
      state['HEAP8'].set(bytes, ptr);
      this.loadGame(ptr, bytes.length);
      // No freeSaveFile: the pointer is stack-allocated (BR-125).
    };

    load(saveString);

    // BR-140: load_game bypasses the boardSizePreset/options setters, so
    // rehydrate the two things the setter path normally provides:
    // 1. The cached solution used by hints. _precacheSolution() can't be used
    //    directly (it would solve the resumed position, and its restart()
    //    would then lose it) — restart to the initial state first (always
    //    solvable, same solution the setters precache), solve, capture, then
    //    reload the resumed position.
    this.restart();
    this.solve();
    this._solution = this._getState('SOLVE')[0];
    load(saveString);
    // 2. Undo/redo UI flags — load_game never calls update_undo_redo, so
    //    refresh them from the restored move list (redo is conservatively
    //    disabled: MOVE lines only run up to statepos).
    this._onUndoRedoEnabled(this.state.length > 0, false);
  }

  restoreCheckpointFrom(saveString) {
    this._checkpoint = saveString;
    this._checkpointMoves = null;
  }

  get checkpoint() {
    return this._checkpoint;
  }

  undo() {
    this.command(7);
  }

  redo() {
    this.command(8);
  }

  newGame() {
    this.clearCheckpoint();
    this.command(5);
    // BR-143: hints must work after New Game — precache the fresh game's
    // solution exactly as the boardSizePreset/options setters do.
    this._precacheSolution();
  }
  solve() { this.command(9); }
  restart() {
    this.clearCheckpoint();
    this.command(6);
  }

  _precacheSolution() {
    this.solve()
    this._solution = this._getState('SOLVE')[0];
    this.restart()
  }

  _getState(tag) {
    const ptr = this._model.getGameState();
    const state = this.UTF8ToString(ptr);
    this.freeSaveFile(ptr);

    return state.split('\n')
                .filter(line => line.startsWith(tag))
                .map(line => line.split(' ').filter(x => !!x)[1]);
  }

  get solution() {
    return this._solution;
  }

  get state() {
    return this._getState('MOVE');
  }

  get rectangles() {
    return this._rectangles;
  }

  get lines() {
    return this._lines;
  }

  applyColourOverrides(colourOverrides) {
    this._colourOverrides = colourOverrides;
  }

  get wasmInterface() {
    return {
      __assert_fail: this.assertFail.bind(this),
      _emscripten_memcpy_js: this.emscriptenMemcpyJs.bind(this),
      emscripten_resize_heap: this.emscriptenResizeHeap.bind(this),
      environ_get: this.environGet.bind(this),
      environ_sizes_get: this.environSizesGet.bind(this),
      js_activate_timer: this.jsActivateTimer.bind(this),
      js_add_preset: this.jsAddPreset.bind(this),
      js_add_preset_submenu: (menuId, ptr) => {},
      js_canvas_clip: this.jsCanvasClip.bind(this),
      js_canvas_draw_circle: this.jsCanvasDrawCircle.bind(this),
      js_canvas_draw_line: this.jsCanvasDrawLine.bind(this),
      js_canvas_draw_poly: this.jsCanvasDrawPoly.bind(this),
      js_canvas_draw_rect: this.jsCanvasDrawRect.bind(this),
      js_canvas_draw_text: this.jsCanvasDrawText.bind(this),
      js_canvas_draw_update: this.jsCanvasDrawUpdate.bind(this),
      js_canvas_end_draw: this.jsCanvasEndDraw.bind(this),
      js_canvas_find_font_midpoint: this.jsCanvasFindFontMidpoint.bind(this),
      js_canvas_free_blitter: this.jsCanvasFreeBlitter.bind(this),
      js_canvas_copy_to_blitter: this.jsCanvasCopyToBlitter.bind(this),
      js_canvas_copy_from_blitter: this.jsCanvasCopyFromBlitter.bind(this),
      js_canvas_get_preferred_size: this.jsCanvasGetPreferredSize.bind(this),
      js_canvas_new_blitter: this.jsCanvasNewBlitter.bind(this),
      js_canvas_make_statusbar: () => {},
      js_canvas_remove_statusbar: () => {},
      js_canvas_set_size: this.jsCanvasSetSize.bind(this),
      js_canvas_start_draw: this.jsCanvasStartDraw.bind(this),
      js_canvas_set_statusbar: () => {},
      js_canvas_clip_rect: this.jsCanvasClipRect.bind(this),
      js_canvas_unclip: this.jsCanvasUnclip.bind(this),
      js_deactivate_timer: this.jsDeactivateTimer.bind(this),
      js_default_colour: this.jsDefaultColour.bind(this),
      js_dialog_boolean: (init, title, initValue) => {},
      js_dialog_choices: (index, title, choiceList, initValue) => {},
      js_dialog_cleanup: () => {},
      js_dialog_init: (titleText) => {},
      js_dialog_launch: () => {},
      js_dialog_string: (index, title, initialText) => {},
      js_enable_undo_redo: (undo, redo) => this._onUndoRedoEnabled(this.state.length > 0 && undo === 1, redo === 1),
      js_error_box: (ptr) => console.error(this.UTF8ToString(ptr)),
      js_focus_canvas: this.jsFocusCanvas.bind(this),
      js_get_date_64: this.jsGetDate64.bind(this),
      js_get_device_pixel_ratio: this.jsGetDevicePixelRatio.bind(this),
      js_get_selected_preset: this.jsGetSelectedPreset.bind(this),
      js_load_prefs: (me) => {},
      js_post_init: this.jsPostInit.bind(this),
      js_remove_solve_button: () => {},
      js_remove_type_dropdown: () => {},
      js_savefile_read: this.jsSaveFileRead.bind(this),
      js_select_preset: this.jsSelectPreset.bind(this),
      js_update_key_labels: (lskPtr, cskPtr) => {},
      js_update_permalinks: (desc, seed) => {},
      js_new_game: this.jsNewGame.bind(this),
      js_move: this.jsMove.bind(this),
      js_completed: this.jsCompleted.bind(this),
    };
  }

  /**
   * Assumes x and y offset (i.e. top and left margins) are equal.
   */
  highlightRectangle(invalidBox, columnCount, rowCount, canvasWidth, canvasHeight, offset, colour) {
    const xScale = canvasHeight / rowCount;
    const yScale = canvasWidth / columnCount;

    const x = invalidBox.x * xScale + offset;
    const y = invalidBox.y * yScale + offset;
    const w = invalidBox.w * yScale;
    const h = invalidBox.h * xScale;

    // render empty rectangle with flashing border
    const width = 8;
    const ctx = this.onscreenCanvas.getContext('2d', { alpha: false });

    function drawLine(x1, y1, x2, y2) {
      ctx.beginPath();
      ctx.moveTo(x1 + 0.5, y1 + 0.5);
      ctx.lineTo(x2 + 0.5, y2 + 0.5);
      ctx.lineWidth = width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = colour;
      ctx.stroke();
      ctx.fillStyle = colour;
      ctx.fillRect(x1, y1, 1, 1);
      ctx.fillRect(x2, y2, 1, 1);
    }

    // flashing effect
    const rectData = ctx.getImageData(x - 10, y - 10, w + 20, h + 20);
    function draw() {
      drawLine(x, y, x + w, y); // top
      drawLine(x + w, y, x + w, y + h); // right
      drawLine(x + w, y + h, x, y + h); // bottom
      drawLine(x, y + h, x, y); // left

      setTimeout(reset, interval);
    }

    function reset() {
      ctx.putImageData(rectData, x - 10, y - 10);

      if (++i < 2) {
        setTimeout(draw, interval);
      }
    }

    const interval = 350;
    let i = 0;
    draw();
  }
}

export default ViewModel;
